import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Ticket, Wallet, Users, FileBarChart, LogOut, Bell, CalendarClock,
  Settings, Package, ClipboardList, Megaphone, MapPin, ClipboardCheck,
  Compass, Wrench, UsersRound, ChevronDown, Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/format";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import UpdateBanner from "@/components/UpdateBanner";

// Turns a notification into { path, label } for deep-link routing from the bell.
function notifTarget(n) {
  const data = n.data || {};
  switch (n.kind) {
    case "leave_applied":
      return { path: `/roster?tab=grid&highlight_leave=${data.leave_id || ""}${data.department ? `&department=${data.department}` : ""}`, label: "Leave request • tap to review" };
    case "leave_decided":
      return { path: `/roster?tab=mine&highlight_leave=${data.leave_id || ""}`, label: "Leave update • tap to view" };
    case "roster_transfer":
      return {
        path: `/roster?tab=grid&highlight_shift=${data.shift_id || ""}${data.to_outlet_id ? `&outlet_id=${data.to_outlet_id}` : ""}${data.department ? `&department=${data.department}` : ""}${data.date ? `&date=${data.date}` : ""}`,
        label: "Roster transfer • tap to view",
      };
    case "notice":
      return { path: `/notices`, label: "New notice • tap to read" };
    case "visit_completed":
      return { path: `/admin/visits`, label: "Visit report ready • tap to review" };
    case "visit_decided":
      return { path: `/planner`, label: `Visit ${data.status || "reviewed"} • tap to view` };
    case "escalation":
      return { path: n.ticket_id ? `/tickets/${n.ticket_id}` : "/tickets", label: "Escalation • tap to view" };
    default:
      return { path: n.ticket_id ? `/tickets/${n.ticket_id}` : "/", label: "Tap to view" };
  }
}

// Grouped navigation. Every leaf carries the same role gate we had before —
// each group is hidden entirely when the user cannot see any of its items.
const ALL_ROLES = ["admin","manager","op_manager","hod","facility_incharge","outlet_incharge","outlet_staff"];
const NAV_GROUPS = [
  {
    key: "overview",
    label: "Overview",
    icon: Compass,
    items: [
      { to: "/",         label: "Dashboard", icon: LayoutDashboard, roles: ["admin","manager","op_manager","facility_incharge","outlet_incharge","outlet_staff"] },
      { to: "/notices",  label: "Notices",   icon: Megaphone,       roles: ALL_ROLES },
    ],
  },
  {
    key: "facility",
    label: "Facility Track",
    icon: Wrench,
    items: [
      { to: "/tickets",   label: "Tickets",   icon: Ticket,        roles: ["admin","manager","op_manager","facility_incharge","outlet_incharge","outlet_staff"] },
      { to: "/payments",  label: "Payments",  icon: Wallet,        roles: ["admin","manager","op_manager","facility_incharge"] },
      { to: "/recurring", label: "Recurring", icon: CalendarClock, roles: ["admin","manager","op_manager"] },
      { to: "/inventory", label: "Inventory", icon: Package,       roles: ["admin","manager","op_manager","facility_incharge"] },
    ],
  },
  {
    key: "people",
    label: "Employee Track",
    icon: UsersRound,
    items: [
      { to: "/roster",        label: "Duty Roster",     icon: ClipboardList,  roles: ALL_ROLES },
      { to: "/planner",       label: "Monthly Planner", icon: MapPin,         roles: ["op_manager","admin"] },
      { to: "/admin/visits",  label: "Visit Approvals", icon: ClipboardCheck, roles: ["admin"] },
    ],
  },
];
// Kept outside the groups on purpose — these are system-admin surfaces the
// user asked to leave alone, so they stay as direct rail buttons.
const EXTRA_ITEMS = [
  { to: "/manage",  label: "Manage",  icon: Users,        roles: ["admin","manager","op_manager"] },
  { to: "/reports", label: "Reports", icon: FileBarChart, roles: ["admin","manager","op_manager"] },
];

function useVisibleGroups(role) {
  return NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => i.roles.includes(role)) }))
    .filter((g) => g.items.length > 0);
}

function GroupTrigger({ group, active }) {
  const Icon = group.icon;
  return (
    <DropdownMenuTrigger
      data-testid={`nav-group-${group.key}`}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors outline-none ${
        active ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      <Icon className="w-4 h-4" />
      {group.label}
      <ChevronDown className="w-3.5 h-3.5 opacity-60" />
    </DropdownMenuTrigger>
  );
}

function GroupDropdown({ group, currentPath, onGo }) {
  const active = group.items.some((i) => currentPath === i.to || (i.to !== "/" && currentPath.startsWith(i.to)));
  return (
    <DropdownMenu>
      <GroupTrigger group={group} active={active} />
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">{group.label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {group.items.map((n) => {
          const Icon = n.icon;
          const isActive = currentPath === n.to || (n.to !== "/" && currentPath.startsWith(n.to));
          return (
            <DropdownMenuItem
              key={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => onGo(n.to)}
              className={`gap-2 ${isActive ? "bg-indigo-50 text-indigo-700 focus:bg-indigo-50" : ""}`}
            >
              <Icon className="w-4 h-4" /> {n.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarLink({ to, icon: Icon, label, onClose }) {
  return (
    <SheetClose asChild>
      <NavLink
        to={to}
        end={to === "/"}
        data-testid={`snav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={onClose}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-700 hover:bg-slate-100"
          }`
        }
      >
        <Icon className="w-4 h-4" /> {label}
      </NavLink>
    </SheetClose>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [notes, setNotes] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (alive) setNotes(data);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const role = user?.role;
  const groups = useVisibleGroups(role);
  const extras = EXTRA_ITEMS.filter((n) => n.roles.includes(role));
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

  const markAllRead = async () => {
    for (const n of notes) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setNotes([]);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <UpdateBanner />
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center gap-4">
          {/* Mobile: hamburger opens the grouped sidebar drawer */}
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                className="md:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100"
                aria-label="Open navigation"
              >
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col">
              <SheetHeader className="px-4 py-4 border-b border-slate-100 text-left">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-xl bg-indigo-600 grid place-items-center text-white">
                    <Ticket className="w-5 h-5" />
                  </div>
                  <div>
                    <SheetTitle className="text-sm font-display font-bold text-slate-900 leading-none">Job Ticker</SheetTitle>
                    <div className="text-[11px] text-slate-500 leading-tight mt-0.5">Facility Ops</div>
                  </div>
                </div>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
                {groups.map((g) => (
                  <div key={g.key}>
                    <div className="flex items-center gap-2 px-3 mb-1.5">
                      <g.icon className="w-3.5 h-3.5 text-slate-400" />
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{g.label}</div>
                    </div>
                    <div className="space-y-0.5">
                      {g.items.map((n) => (
                        <SidebarLink key={n.to} to={n.to} icon={n.icon} label={n.label} onClose={() => setSidebarOpen(false)} />
                      ))}
                    </div>
                  </div>
                ))}
                {extras.length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-3 mb-1.5">System</div>
                    <div className="space-y-0.5">
                      {extras.map((n) => (
                        <SidebarLink key={n.to} to={n.to} icon={n.icon} label={n.label} onClose={() => setSidebarOpen(false)} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-indigo-600 grid place-items-center text-white shadow-sm">
              <Ticket className="w-5 h-5" />
            </div>
            <div className="hidden sm:block">
              <div className="font-display font-bold text-slate-900 leading-none">Job Ticker</div>
              <div className="text-[11px] text-slate-500 leading-tight">Facility Ops</div>
            </div>
          </div>

          {/* Desktop grouped nav */}
          <nav className="hidden md:flex items-center gap-1 ml-6">
            {groups.map((g) => (
              <GroupDropdown key={g.key} group={g} currentPath={location.pathname} onGo={nav} />
            ))}
            {extras.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" /> {n.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="notif-btn" className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
                <Bell className="w-5 h-5 text-slate-600" />
                {notes.length > 0 && (
                  <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Notifications</span>
                  {notes.length > 0 && (
                    <button data-testid="notif-mark-read" onClick={markAllRead} className="text-xs font-medium text-indigo-600 hover:underline">
                      Mark all read
                    </button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notes.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-slate-500">You're all caught up.</div>
                ) : notes.slice(0, 8).map((n) => {
                  const { path, label } = notifTarget(n);
                  return (
                    <DropdownMenuItem
                      key={n.id}
                      data-testid={`notif-item-${n.id}`}
                      onClick={async () => {
                        try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
                        setNotes((prev) => prev.filter((x) => x.id !== n.id));
                        nav(path);
                      }}
                      className="flex flex-col items-start gap-0.5"
                    >
                      <div className="text-sm font-medium text-slate-800">{n.title || n.message}</div>
                      {n.title && n.message !== n.title && (
                        <div className="text-[11px] text-slate-600 line-clamp-2">{n.message}</div>
                      )}
                      <div className="text-[11px] text-slate-500">{label}</div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100 transition-colors">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 grid place-items-center text-white text-xs font-semibold">
                  {initials.toUpperCase()}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-xs font-semibold text-slate-800 leading-tight">{user?.name}</div>
                  <div className="text-[10px] text-slate-500 leading-tight">{ROLE_LABEL[user?.role]}</div>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="text-sm text-slate-900">{user?.email}</div>
                  <div className="text-xs text-slate-500 font-normal">{ROLE_LABEL[user?.role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-settings" onClick={() => nav("/settings")}>
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-rose-600">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-6 pb-8">
        {children}
      </main>
    </div>
  );
}
